import { useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  FaHome,
  FaFileContract,
  FaCalendarAlt,
  FaCheckCircle,
  FaArrowRight,
  FaGraduationCap,
  FaMoneyBillWave,
  FaUserShield,
  FaClock,
  FaBuilding,
  FaExclamationTriangle,
  FaGavel,
  FaEnvelope,
  FaPhone,
  FaArrowUp,
  FaUsers,
  FaBook,
  FaShieldAlt,
  FaFileSignature,
  FaEdit,
  FaFileContract as FaFileContractIcon,
} from "react-icons/fa";
import { title as siteTitle } from "../constants/constant";
import Layout from "../components/layout/Layout";
import BreadcrumbImg from "../assets/images/breadcrumb.png";

// Constants Data
const breadcrumbData = {
  home: { text: "Home", path: "/" },
  current: "Terms & Conditions",
  title: "Terms & Conditions 2026-27",
  image: BreadcrumbImg,
};

const headerInfo = {
  effectiveDate: "April 1, 2026",
  academicYear: "2026-27",
  description:
    "These Terms & Conditions establish the rules and regulations governing the relationship between the school, students, and parents/guardians. They are binding from the date of admission and throughout the academic journey at our institution.",
};

const keyDefinitions = [
  {
    term: "School",
    definition: "Refers to Gyan Rashmi and its administration",
  },
  {
    term: "Parent/Guardian",
    definition: "Legal guardian responsible for student",
  },
  { term: "Academic Year", definition: "April to March as per CBSE calendar" },
  {
    term: "School Hours",
    definition: "8:30 AM to 2:30 PM unless notified otherwise",
  },
  {
    term: "Official Communication",
    definition: "School notice board, website, or registered email",
  },
];

const sections = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    icon: FaFileContract,
    color: "blue",
    content:
      "By enrolling at our school, parents/guardians and students agree to abide by these Terms & Conditions. These terms govern the relationship between the school, students, and parents/guardians.",
    points: [
      "Effective from date of admission confirmation",
      "Applicable throughout the academic journey",
      "Binding agreement for all parties involved",
      "Subject to periodic updates and revisions",
    ],
    sectionNumber: "Section 1 of 12",
  },
  {
    id: "admission",
    title: "2. Admission & Enrollment",
    icon: FaGraduationCap,
    color: "emerald",
    content: "Admission to our school is subject to specific terms:",
    bulletPoints: [
      "Admission is based on availability of seats and eligibility criteria",
      "Submission of accurate and complete documentation is mandatory",
      "Provision of false information may lead to cancellation of admission",
      "Admission fee is non-refundable once paid",
      "School reserves right to refuse admission without assigning reason",
    ],
    sectionNumber: "Section 2 of 12",
  },
  {
    id: "academic",
    title: "3. Academic Requirements",
    icon: FaBook,
    color: "purple",
    content: "Students must meet academic standards and requirements:",
    points: [
      "Regular attendance as per school policy",
      "Completion of all assignments and projects",
      "Participation in examinations and assessments",
      "Maintenance of minimum academic performance standards",
      "Compliance with CBSE curriculum guidelines",
    ],
    sectionNumber: "Section 3 of 12",
  },
  {
    id: "fees",
    title: "4. Fee Payment Terms",
    icon: FaMoneyBillWave,
    color: "amber",
    content: "Financial obligations and payment terms:",
    bulletPoints: [
      "Fees must be paid by due dates specified in fee schedule",
      "Late payments may attract penalty charges",
      "No reduction in fees for absence or holidays",
      "Fee structure subject to periodic revision with notice",
      "Outstanding fees may result in withholding of results/certificates",
    ],
    sectionNumber: "Section 4 of 12",
  },
  {
    id: "conduct",
    title: "5. Student Conduct & Discipline",
    icon: FaUserShield,
    color: "blue",
    content: "Students are expected to maintain high standards of conduct:",
    points: [
      "Respect for teachers, staff, and fellow students",
      "Adherence to school uniform and appearance guidelines",
      "Prohibition of bullying, harassment, or disruptive behavior",
      "Care for school property and facilities",
      "Compliance with safety and security protocols",
    ],
    sectionNumber: "Section 5 of 12",
  },
  {
    id: "attendance",
    title: "6. Attendance Policy",
    icon: FaClock,
    color: "emerald",
    content: "Regular attendance is essential for academic success:",
    bulletPoints: [
      "Minimum 75% attendance required for academic promotion",
      "Leave applications must be submitted in advance",
      "Medical certificates required for extended absence",
      "Unauthorized absence may lead to disciplinary action",
      "Parents must inform school of student absence",
    ],
    sectionNumber: "Section 6 of 12",
  },
  {
    id: "parental",
    title: "7. Parental Responsibilities",
    icon: FaUsers,
    color: "purple",
    content: "Parents/guardians have specific responsibilities:",
    points: [
      "Regular monitoring of student academic progress",
      "Attendance at parent-teacher meetings",
      "Timely response to school communications",
      "Provision of accurate contact information",
      "Support for school policies and decisions",
    ],
    sectionNumber: "Section 7 of 12",
  },
  {
    id: "withdrawal",
    title: "8. Withdrawal & Transfer",
    icon: FaFileSignature,
    color: "amber",
    content: "Procedures for student withdrawal or transfer:",
    bulletPoints: [
      "One month written notice required for withdrawal",
      "Clearance certificate needed for all dues and library books",
      "Transfer certificate issued after all formalities completed",
      "No refund of fees for mid-term withdrawals",
      "School records released only after complete clearance",
    ],
    sectionNumber: "Section 8 of 12",
  },
  {
    id: "property",
    title: "9. School Property & Facilities",
    icon: FaBuilding,
    color: "blue",
    content: "Usage and care of school property:",
    points: [
      "Students responsible for care of school property",
      "Damage to property must be compensated",
      "Proper use of library, laboratory, and sports facilities",
      "Prohibition of unauthorized entry to restricted areas",
      "Technology resources for educational purposes only",
    ],
    sectionNumber: "Section 9 of 12",
  },
  {
    id: "liability",
    title: "10. Limitation of Liability",
    icon: FaShieldAlt,
    color: "emerald",
    content: "School's liability limitations:",
    bulletPoints: [
      "School not liable for loss of personal belongings",
      "Limited liability for accidents during school activities",
      "Not responsible for transportation delays or issues",
      "Medical emergencies handled as per school policy",
      "Force majeure events beyond school's control",
    ],
    sectionNumber: "Section 10 of 12",
  },
  {
    id: "amendments",
    title: "11. Amendments & Updates",
    icon: FaEdit,
    color: "purple",
    content: "Terms may be updated periodically:",
    points: [
      "School reserves right to amend terms with notice",
      "Changes communicated through official channels",
      "Continued enrollment implies acceptance of updated terms",
      "Major changes communicated to parents in writing",
      "Current version always available on school website",
    ],
    sectionNumber: "Section 11 of 12",
  },
  {
    id: "governing",
    title: "12. Governing Law & Jurisdiction",
    icon: FaGavel,
    color: "amber",
    content: "Legal framework for these terms:",
    bulletPoints: [
      "Terms governed by laws of India",
      "Disputes subject to jurisdiction of local courts",
      "CBSE regulations supersede where applicable",
      "Education department guidelines apply",
      "English version prevails for interpretation",
    ],
    sectionNumber: "Section 12 of 12",
  },
];

const importantClauses = [
  "These terms supplement the school admission form and prospectus",
  "School rules and regulations are binding on all students",
  "Special circumstances considered on case-by-case basis",
  "School management decisions are final in all matters",
  "Digital communication considered valid and binding",
];

const contactInfo = {
  email: "admin@school.edu",
  phone: "+91 1234567890",
  hours: [
    "Monday - Friday: 8:00 AM - 4:00 PM",
    "Saturday: 8:00 AM - 1:00 PM",
    "Sunday: Closed",
  ],
};

export default function Terms() {
  // Handle smooth scrolling for anchor links
  useEffect(() => {
    const handleHashChange = () => {
      const { hash } = window.location;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    };

    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Layout>
      <Helmet>
        <title>Terms & Conditions | {siteTitle}</title>
        <meta
          name="description"
          content="Read our terms and conditions governing admissions, fee payment, student conduct, attendance policy, and more at Gyan Rashmi."
        />
      </Helmet>

      {/* Breadcrumb Section */}
      <div className="relative bg-gray-900">
        <div className="h-40 md:h-48">
          <img
            alt="Breadcrumb"
            className="object-cover w-full h-full opacity-60"
            src={breadcrumbData.image}
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="flex items-center mb-2 text-sm text-white">
            <a
              href={breadcrumbData.home.path}
              className="flex items-center transition-colors hover:text-gray-200"
            >
              <FaHome className="w-3 h-3 mr-1.5" />
              {breadcrumbData.home.text}
            </a>
            <span className="mx-2">/</span>
            <span className="font-semibold">{breadcrumbData.current}</span>
          </div>
          <h1 className="text-2xl font-bold text-white md:text-3xl">
            {breadcrumbData.title}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-3 py-5 mx-auto max-w-7xl md:px-4 md:py-6">
        {/* Header Section */}
        <div className="p-4 mb-5 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
          <div className="flex flex-col gap-3 md:flex-row md:items-start">
            <div className="md:w-2/3">
              <div className="flex items-center gap-2 mb-2">
                <FaFileContractIcon className="w-5 h-5 text-blue-600" />
                <h2 className="text-base font-bold text-gray-900">
                  {breadcrumbData.title}
                </h2>
              </div>
              <p className="text-xs text-gray-700">{headerInfo.description}</p>
            </div>
            <div className="md:w-1/3">
              <div className="p-3 bg-white border border-blue-100 rounded-lg">
                <div className="flex items-center gap-1.5 mb-1">
                  <FaCalendarAlt className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-xs font-semibold text-gray-900">
                    Effective Date
                  </span>
                </div>
                <div className="text-sm font-medium text-blue-700">
                  {headerInfo.effectiveDate}
                </div>
                <div className="mt-1 text-[10px] text-gray-600">
                  Academic Year {headerInfo.academicYear}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Definitions */}
        <div className="p-4 mb-5 border border-gray-200 bg-gray-50 rounded-xl">
          <h3 className="mb-3 text-xs font-bold text-gray-900">
            Key Definitions
          </h3>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-5">
            {keyDefinitions.map((item, idx) => (
              <div
                key={idx}
                className="p-2 bg-white border border-gray-200 rounded-lg"
              >
                <div className="mb-0.5 text-xs font-semibold text-blue-700">
                  {item.term}
                </div>
                <div className="text-[10px] text-gray-600">
                  {item.definition}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Terms Sections */}
        <div className="mb-5 space-y-4">
          {sections.map((section) => (
            <div key={section.id} id={section.id} className="scroll-mt-20">
              <div className="overflow-hidden transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-sm">
                <div className={`p-4 border-b border-gray-200 bg-gray-50`}>
                  <div className="flex items-center gap-2">
                    <div
                      className={`p-1.5 rounded-lg bg-${section.color}-100 text-${section.color}-600`}
                    >
                      <section.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-gray-900">
                        {section.title}
                      </h3>
                    </div>
                    <div className="text-[10px] font-medium text-gray-500">
                      {section.sectionNumber}
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="mb-3 text-xs text-gray-700">
                    {section.content}
                  </p>
                  {section.points && (
                    <ul className="space-y-1.5">
                      {section.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-1.5">
                          <div
                            className={`w-1 h-1 mt-1.5 rounded-full flex-shrink-0 bg-${section.color}-500`}
                          ></div>
                          <span className="text-xs text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.bulletPoints && (
                    <ul className="space-y-1.5">
                      {section.bulletPoints.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-1.5">
                          <FaArrowRight
                            className={`w-2.5 h-2.5 mt-1 flex-shrink-0 text-${section.color}-500`}
                          />
                          <span className="text-xs text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Important Clauses */}
        <div className="p-4 mb-5 border bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <FaExclamationTriangle className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-bold text-gray-900">
              Important Clauses
            </h3>
          </div>
          <div className="grid gap-2">
            {importantClauses.map((clause, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 p-2 rounded-lg bg-white/50"
              >
                <div className="flex items-center justify-center flex-shrink-0 w-5 h-5 rounded-full bg-amber-100">
                  <span className="text-[8px] font-bold text-amber-700">
                    {idx + 1}
                  </span>
                </div>
                <p className="text-xs text-gray-700">{clause}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Acceptance Section */}
        <div className="p-4 mb-5 border border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 mb-3 bg-white rounded-full">
              <FaCheckCircle className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="mb-2 text-sm font-bold text-gray-900">
              Acceptance of Terms
            </h3>
            <p className="mb-3 text-xs text-gray-700">
              By proceeding with admission and enrollment, parents/guardians
              acknowledge that they have read, understood, and agree to be bound
              by these Terms & Conditions. Continued enrollment constitutes
              ongoing acceptance of these terms.
            </p>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600">
              <FaCalendarAlt className="w-3 h-3" />
              <span>
                These terms are effective for the entire duration of enrollment
              </span>
            </div>
          </div>
        </div>

        {/* Questions & Office Hours */}
        <div className="p-4 mb-5 bg-white border border-gray-200 rounded-xl">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="mb-2 text-sm font-bold text-gray-900">
                Questions & Clarifications
              </h3>
              <p className="mb-3 text-xs text-gray-600">
                For any questions regarding these Terms & Conditions, please
                contact the school administration.
              </p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <FaEnvelope className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-xs text-gray-700">
                    {contactInfo.email}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FaPhone className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-xs text-gray-700">
                    {contactInfo.phone}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-bold text-gray-900">
                Office Hours
              </h3>
              <div className="space-y-1 text-xs text-gray-600">
                {contactInfo.hours.map((hour, idx) => (
                  <p key={idx}>{hour}</p>
                ))}
              </div>
              <div className="p-2 mt-3 rounded-lg bg-blue-50">
                <p className="text-[10px] text-blue-700">
                  For urgent matters during non-office hours, please refer to
                  emergency contact numbers provided during admission.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Top */}
        <div className="text-center">
          <button
            onClick={scrollToTop}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Back to Top
            <FaArrowUp className="w-3 h-3" />
          </button>
        </div>
      </div>
    </Layout>
  );
}
